/**
 * PacienteNoContestaBanner — aviso al médico cuando está en el room y el
 * paciente NO ha contestado la videollamada.
 *
 * Aparece a los 20 s de espera (basado en los mismos campos que usa el cron
 * verificarNoShows: medicoEnRoom / pacienteEnRoom / pacienteJoinedAt) con:
 *  - Tiempo esperando + cuenta regresiva del cierre automático (20 min).
 *  - "Volver a llamar": re-envía el push de llamada (portal_reintentarLlamadaPaciente),
 *    con cooldown de 20 s; cada intento queda en auditoría.
 *  - "No contestó — Finalizar": abre el modal de finalización para cerrar con
 *    la disposición "Paciente no se presentó".
 * Desaparece solo cuando el paciente entra al room.
 */
import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { BellRing, PhoneMissed, UserX } from 'lucide-react';
import { functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import type { Consulta } from '../../types';

const reintentarLlamadaFn = httpsCallable<
  { consultaId: string },
  { success: boolean; intento: number; dispositivos: number; aceptados: number }
>(functions, 'portal_reintentarLlamadaPaciente');

const MOSTRAR_TRAS_SEG = 20;
const AUTOCIERRE_SEG = 20 * 60; // debe coincidir con NO_SHOW_TIMEOUT_MINS del cron
const COOLDOWN_SEG = 20;

interface Props {
  consultaId: string;
  consulta: Consulta | null | undefined;
  videoStarted: boolean;
  onFinalizar: () => void;
}

export function PacienteNoContestaBanner({ consultaId, consulta, videoStarted, onFinalizar }: Props) {
  const { addToast } = useToast();
  const [ahora, setAhora] = useState(() => Date.now());
  const [reintentando, setReintentando] = useState(false);
  const [cooldownHasta, setCooldownHasta] = useState(0);

  const esperando = !!(
    videoStarted &&
    consulta &&
    consulta.estado === 'en_atencion' &&
    consulta.medicoEnRoom === true &&
    consulta.pacienteEnRoom !== true &&
    !consulta.pacienteJoinedAt &&
    consulta.videoFinalizado !== true
  );

  useEffect(() => {
    if (!esperando) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [esperando]);

  const segundosEsperando = useMemo(() => {
    const inicio = consulta?.medicoJoinedAt?.toMillis?.();
    if (!inicio) return 0;
    return Math.max(0, Math.floor((ahora - inicio) / 1000));
  }, [ahora, consulta?.medicoJoinedAt]);

  // Rechazo explícito: se muestra DE INMEDIATO (sin esperar los 20 s)
  const rechazada = esperando && consulta?.llamadaRechazada === true;

  if (!esperando || (!rechazada && segundosEsperando < MOSTRAR_TRAS_SEG)) return null;

  const restanteAutoCierre = Math.max(0, AUTOCIERRE_SEG - segundosEsperando);
  const mmss = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const enCooldown = ahora < cooldownHasta;

  const reintentar = async () => {
    setReintentando(true);
    try {
      const res = await reintentarLlamadaFn({ consultaId });
      setCooldownHasta(Date.now() + COOLDOWN_SEG * 1000);
      addToast('success',
        `Llamada reenviada (intento #${res.data.intento}) a ${res.data.dispositivos} dispositivo${res.data.dispositivos === 1 ? '' : 's'} del paciente.`);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo reenviar la llamada.');
    } finally {
      setReintentando(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 72,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1150,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: rechazada ? 'var(--color-error-bg)' : 'var(--color-warning-bg)',
        border: `1px solid ${rechazada ? 'var(--color-error)' : 'var(--color-warning)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '10px 14px',
        boxShadow: 'var(--shadow-card-hover)',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <PhoneMissed
        size={18}
        style={{ color: rechazada ? 'var(--color-error)' : 'var(--color-warning)', flexShrink: 0 }}
        aria-hidden="true"
      />
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {rechazada
            ? `El paciente RECHAZÓ la llamada${(consulta?.rechazosLlamada ?? 0) > 1 ? ` (${consulta?.rechazosLlamada} veces)` : ''}`
            : `El paciente aún no contesta (${mmss(segundosEsperando)})`}
        </p>
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {consulta?.llamadaIntentos && consulta.llamadaIntentos > 1
            ? `${consulta.llamadaIntentos} llamadas enviadas · `
            : ''}
          {rechazada
            ? 'Puede volver a llamar o finalizar la consulta con "Paciente rechazó la llamada".'
            : `Cierre automático por no-contestación en ${mmss(restanteAutoCierre)}`}
        </p>
      </div>
      <button
        type="button"
        onClick={() => void reintentar()}
        disabled={reintentando || enCooldown}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: '1px solid var(--color-warning)', background: 'var(--color-surface)',
          color: 'var(--color-text-primary)', borderRadius: 'var(--radius-md)',
          padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 600,
          cursor: reintentando || enCooldown ? 'not-allowed' : 'pointer',
          opacity: reintentando || enCooldown ? 0.6 : 1, whiteSpace: 'nowrap',
        }}
      >
        <BellRing size={13} aria-hidden="true" />
        {reintentando ? 'Enviando…' : enCooldown ? `Espere ${Math.ceil((cooldownHasta - ahora) / 1000)}s` : 'Volver a llamar'}
      </button>
      <button
        type="button"
        onClick={onFinalizar}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: 'none', background: 'var(--color-error)', color: '#fff',
          borderRadius: 'var(--radius-md)', padding: '6px 12px',
          fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <UserX size={13} aria-hidden="true" /> No contestó — Finalizar
      </button>
    </div>
  );
}
